import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useState, useEffect } from "react";
import onboardingService from "../services/onboardingService";
import { PermissionManager } from "../utils/auth";

function ProtectedRoute({
  children,
  requireOnboarding = true,
  requiredPermission = null,
}) {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  // Check onboarding status when user is authenticated
  useEffect(() => {
    let isMounted = true;

    const checkOnboarding = async () => {
      if (!isAuthenticated || !user || loading || onboardingLoading) {
        return;
      }

      setOnboardingLoading(true);

      try {
        const status = await onboardingService.checkOnboardingStatus();
        if (isMounted) {
          setOnboardingStatus(status);
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
        if (isMounted) {
          setOnboardingStatus({ isCompleted: false, hasProfile: false });
        }
      } finally {
        if (isMounted) {
          setOnboardingLoading(false);
        }
      }
    };

    // Reset onboarding status when user changes
    setOnboardingStatus(null);
    checkOnboarding();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user?.id, loading]);

  // Show loading only for auth loading, not onboarding loading
  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
        <p className="text-gray-600">Verifying authentication...</p>
      </div>
    );
  }

  // Check authentication
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions if required
  if (requiredPermission && user.permissions) {
    if (
      !PermissionManager.hasPermission(user.permissions, requiredPermission)
    ) {
      return (
        <div className="flex flex-col justify-center items-center min-h-64">
          <div className="text-red-600 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 text-center max-w-md">
            You don&apos;t have permission to access this page. Please contact your
            administrator if you believe this is an error.
          </p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            Go Back
          </button>
        </div>
      );
    }
  }

  // Handle onboarding logic - only if we have onboarding status and require onboarding
  if (requireOnboarding) {
    const isOnboardingRoute = location.pathname === "/onboarding";

    // If still loading onboarding status, show loading for onboarding routes only
    if (onboardingLoading && isOnboardingRoute) {
      return (
        <div className="flex flex-col justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-gray-600">Loading onboarding...</p>
        </div>
      );
    }

    // If we have onboarding status
    if (onboardingStatus) {
      // If user hasn't completed onboarding and is not on onboarding page, redirect to onboarding
      if (!onboardingStatus.isCompleted && !isOnboardingRoute) {
        return <Navigate to="/onboarding" replace />;
      }

      // If user has completed onboarding and is on onboarding page, redirect to dashboard
      if (onboardingStatus.isCompleted && isOnboardingRoute) {
        return <Navigate to="/dashboard" replace />;
      }
    }
  }

  return children;
}

export default ProtectedRoute;
